import React, { useEffect, useRef, useState } from "react"

// Compact SVG chart shown under a Clara answer: the signal trend, the threshold
// line(s), and shaded episode bands. Data comes from the /query result (trend +
// episodes + the query's threshold(s)), all in epoch-ms / value units.
// `thresholds` is a list so a range query draws both the low and high lines.
//
// The x axis spans the WINDOW THE ANSWER IS ABOUT (windowStart to windowEnd), not the
// extent of the data. Scaling to the data made a 9.5 minute episode, 4% of a 4 hour
// window, fill the whole plot, so the chart said the opposite of the sentence above it.
// Axes are drawn so the chart still means something once it is copied out of the chat.

// Drawn at the size it is shown, not scaled from a fixed viewBox: scaling a 640 wide
// drawing up to the width of the chat made the axis labels grow with it. The chart takes
// the width of the conversation column (UTSW NSICU: "the chat only uses maybe a third of
// the window for the graph") and a fixed height, and the text stays the same size.
const DEFAULT_WIDTH = 640
const DEFAULT_HEIGHT = 340
const PAD_L = 52
const PAD_R = 16
const PAD_T = 22
const PAD_B = 34
const Y_TICKS = 5
const PX_PER_X_TICK = 150
const FONT = 12

function niceStep(span, count) {
	const raw = span / Math.max(1, count)
	const magnitude = Math.pow(10, Math.floor(Math.log10(raw)))
	const normalized = raw / magnitude
	const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
	return nice * magnitude
}

/* "EDT", "CST": shown on the time axis so the reader knows which clock it is. */
export function zoneLabel(ms, timeZone) {
	if (!timeZone) return ""
	try {
		const parts = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" }).formatToParts(new Date(ms))
		return parts.find(part => part.type === "timeZoneName")?.value || ""
	} catch (error) {
		return ""
	}
}

/* Times are printed in the RECORDING's timezone when it is known, like Data Review's
   axis, never silently in the browser's (see recordingTime.js). */
export function formatTick(ms, spanMs, timeZone) {
	const date = new Date(ms)
	const zone = timeZone ? { timeZone } : {}
	const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false, ...zone })
	if (spanMs <= 24 * 3_600_000) {
		return time
	}
	return `${date.toLocaleDateString([], { day: "numeric", month: "short", ...zone })} ${time}`
}

export function chartDomain(trend, windowStart, windowEnd) {
	const start = Number(windowStart)
	const end = Number(windowEnd)
	if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
		return [start, end]
	}
	const times = (trend || []).map(p => p[0])
	return [Math.min(...times), Math.max(...times)]
}

function useWidth() {
	const ref = useRef(null)
	const [width, setWidth] = useState(DEFAULT_WIDTH)
	useEffect(() => {
		const node = ref.current
		if (!node || typeof ResizeObserver === "undefined") {
			return undefined
		}
		const observer = new ResizeObserver(entries => {
			const measured = Math.round(entries[0].contentRect.width)
			if (measured > 0) {
				setWidth(measured)
			}
		})
		observer.observe(node)
		return () => observer.disconnect()
	}, [])
	return [ref, width]
}

export function ClaraTrendChart({ trend, episodes = [], crossings = [], thresholds = [], units, windowStart, windowEnd, timeZone, maxWidth = "100%", height = DEFAULT_HEIGHT }) {
	const [frameRef, W] = useWidth()
	const H = height
	const points = (trend || []).filter(p => p[1] !== null && p[1] !== undefined)
	if (points.length < 2) {
		return null
	}
	const X_TICKS = Math.max(3, Math.min(9, Math.floor((W - PAD_L - PAD_R) / PX_PER_X_TICK) + 1))

	const [tMin, tMax] = chartDomain(trend, windowStart, windowEnd)
	const span = tMax - tMin || 1

	// Always draw when there's real data (>= 2 points), even if the signal never crossed
	// its threshold: the trace itself is informative. The y range still includes the
	// threshold so the line stays visible.
	const values = points.map(p => p[1])
	let vMin = Math.min(...values, ...thresholds)
	let vMax = Math.max(...values, ...thresholds)
	if (vMin === vMax) {
		vMin -= 1
		vMax += 1
	}
	const margin = (vMax - vMin) * 0.12
	vMin -= margin
	vMax += margin

	const clampT = t => Math.min(tMax, Math.max(tMin, t))
	const x = t => PAD_L + ((clampT(t) - tMin) / span) * (W - PAD_L - PAD_R)
	const y = v => PAD_T + (1 - (v - vMin) / (vMax - vMin || 1)) * (H - PAD_T - PAD_B)
	const bottom = H - PAD_B

	// Trend polyline segments, broken at gaps (null values) and at points outside the window.
	const segments = []
	let current = []
	for (const [t, v] of trend) {
		if (v === null || v === undefined || t < tMin || t > tMax) {
			if (current.length) {
				segments.push(current)
			}
			current = []
		} else {
			current.push(`${x(t).toFixed(1)},${y(v).toFixed(1)}`)
		}
	}
	if (current.length) {
		segments.push(current)
	}

	const xTicks = Array.from({ length: X_TICKS }, (_, i) => tMin + (span * i) / (X_TICKS - 1))
	const step = niceStep(vMax - vMin, Y_TICKS)
	const yTicks = []
	for (let v = Math.ceil(vMin / step) * step; v <= vMax; v += step) {
		yTicks.push(Number(v.toFixed(6)))
	}

	const axisColor = "var(--text-secondary-color, #6b7280)"
	const gridColor = "var(--color-chart-grid, #d7dde5)"
	const band = (start, end) => ({ x: x(start), width: Math.max(1.5, x(end) - x(start)) })

	return (
		<div ref={frameRef} style={{ width: "100%", maxWidth, marginTop: 12 }}>
		<svg
			viewBox={`0 0 ${W} ${H}`}
			width={W}
			height={H}
			role="img"
			aria-label={`Trend${units ? ` in ${units}` : ""} from ${formatTick(tMin, span, timeZone)} to ${formatTick(tMax, span, timeZone)}${timeZone ? ` ${zoneLabel(tMax, timeZone)}` : ""}`}
			style={{ display: "block" }}
		>
			{yTicks.map(value => (
				<g key={`y-${value}`}>
					<line x1={PAD_L} x2={W - PAD_R} y1={y(value)} y2={y(value)} stroke={gridColor} strokeWidth="0.6" opacity="0.6" />
					<text x={PAD_L - 8} y={y(value) + 4} textAnchor="end" fontSize={FONT} fill={axisColor}>{value}</text>
				</g>
			))}
			<line x1={PAD_L} x2={W - PAD_R} y1={bottom} y2={bottom} stroke={axisColor} strokeWidth="0.8" />
			{xTicks.map((t, index) => (
				<g key={`x-${index}`}>
					<line x1={x(t)} x2={x(t)} y1={bottom} y2={bottom + 4} stroke={axisColor} strokeWidth="0.8" />
					<text
						x={x(t)}
						y={bottom + 20}
						textAnchor={index === 0 ? "start" : index === X_TICKS - 1 ? "end" : "middle"}
						fontSize={FONT}
						fill={axisColor}
					>
						{formatTick(t, span, timeZone)}
					</text>
				</g>
			))}
			{units && (
				<text x={4} y={PAD_T - 8} fontSize={FONT} fill={axisColor}>{units}</text>
			)}
			{timeZone && (
				<text x={W - PAD_R} y={PAD_T - 8} textAnchor="end" fontSize={FONT} fill={axisColor}>
					Times in {zoneLabel(tMax, timeZone) || timeZone}
				</text>
			)}

			{/* Every threshold crossing, shaded faintly (the raw total time). */}
			{crossings.map((crossing, index) => (
				<rect key={`crossing-${index}`} {...band(crossing.start, crossing.end)} y={PAD_T} height={bottom - PAD_T} style={{ fill: "var(--color-accent-interactive, #207dea)" }} opacity="0.1" />
			))}

			{/* The qualifying (sustained) episodes / excursions, shaded strongly on top. */}
			{episodes.map((episode, index) => (
				<rect key={`episode-${index}`} {...band(episode.start, episode.end)} y={PAD_T} height={bottom - PAD_T} style={{ fill: "var(--color-accent-interactive, #207dea)" }} opacity="0.22" />
			))}

			{/* One dashed reference line per threshold (two for a range query). */}
			{thresholds.map((value, index) => (
				<g key={`threshold-${index}`}>
					<line x1={PAD_L} x2={W - PAD_R} y1={y(value)} y2={y(value)} style={{ stroke: "var(--color-danger-text, #e2725b)" }} strokeWidth="1.5" strokeDasharray="5,4" />
					<text x={W - PAD_R} y={y(value) - 4} textAnchor="end" fontSize={FONT + 1} style={{ fill: "var(--color-danger-text, #e2725b)" }}>
						{value}{units ? ` ${units}` : ""}
					</text>
				</g>
			))}

			{segments.map((segment, index) => (
				<polyline key={index} points={segment.join(" ")} fill="none" style={{ stroke: "var(--color-chart-cursor, #207dea)" }} strokeWidth="2" />
			))}
		</svg>
		</div>
	)
}
